/**
 * Keeps remote-print context in sync (branch, designated user, tables, i18n).
 * Actual printing runs in remotePrintSocketHandler via socketService.
 */
import { useContext, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AuthContext } from '../../context/AuthContext';
import { BranchContext } from '../../context/BranchContext';
import { useTranslation } from '../../hooks/useTranslation';
import branchService from '../../services/branchService';
import socketService from '../../services/socketService';
import { selectAllTables } from '../../store/pos/tablesSlice';
import { loadInitialPOSData } from '../../store/pos/thunks';
import {
  isDesignatedRemotePrintUser,
  isPrintStationEnabled,
} from '../../services/remotePrintStation';
import {
  setRemotePrintSocketContext,
  clearRemotePrintSocketContext,
} from '../../services/remotePrintSocketHandler';

const isElectronApp = () =>
  typeof window !== 'undefined' && !!window.electronAPI?.isElectron;

// Re-register every 90 seconds to recover after server restarts clear the in-memory registry.
const REREGISTER_INTERVAL_MS = 90 * 1000;

const RemotePrintSocketBridge = () => {
  const dispatch = useDispatch();
  const { currentUser } = useContext(AuthContext);
  const { branches, selectedBranch } = useContext(BranchContext);
  const { t, language } = useTranslation();
  const tables = useSelector(selectAllTables);
  const selectedBranchId = useSelector((s) => s.branch?.selectedBranchId);
  const resolvedBranchIdRef = useRef(null);

  const resolvedBranchId =
    selectedBranchId ||
    selectedBranch?._id ||
    selectedBranch?.id ||
    currentUser?.branchId ||
    currentUser?.branchIds?.[0] ||
    null;

  resolvedBranchIdRef.current = resolvedBranchId;

  const loadBranchPrintMeta = useCallback(async (branchId) => {
    if (!branchId) return { remotePrintUserId: null, branchMeta: null };
    const fromList = (branches || []).find(
      (b) => String(b._id || b.id) === String(branchId)
    );
    let remotePrintUserId = fromList?.remotePrintUserId ?? null;
    let branchMeta = fromList || null;
    try {
      const fresh = await branchService.getBranchById(branchId);
      remotePrintUserId = fresh?.remotePrintUserId ?? remotePrintUserId;
      branchMeta = fresh || branchMeta;
    } catch (err) {
      console.warn('[print-station] Could not refresh branch print settings:', err);
    }
    return { remotePrintUserId, branchMeta };
  }, [branches]);

  useEffect(() => {
    if (!isElectronApp() || !currentUser || !resolvedBranchId) {
      clearRemotePrintSocketContext();
      return;
    }

    let cancelled = false;

    (async () => {
      const { remotePrintUserId, branchMeta } = await loadBranchPrintMeta(resolvedBranchId);
      if (cancelled) return;

      setRemotePrintSocketContext({
        branchId: resolvedBranchId,
        remotePrintUserId,
        currentUser,
        branchMeta,
      });

      if (isPrintStationEnabled()) {
        socketService.registerPrintStation(resolvedBranchId);
      }

      if (
        isPrintStationEnabled() &&
        isDesignatedRemotePrintUser(remotePrintUserId, currentUser) &&
        tables.length === 0
      ) {
        dispatch(loadInitialPOSData({ branchId: resolvedBranchId }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, resolvedBranchId, loadBranchPrintMeta, dispatch, tables.length]);

  useEffect(() => {
    if (!isElectronApp()) return;
    setRemotePrintSocketContext({ tables, t, language, currentUser });
  }, [tables, t, language, currentUser]);

  // Periodic keepalive: re-connect and re-register after server restarts (which clear
  // the in-memory printStationRegistry). socketService.connect() is a no-op when
  // already connected with the same branchId, so this is safe to call on an interval.
  useEffect(() => {
    if (!isElectronApp()) return;

    const interval = setInterval(() => {
      const bid = resolvedBranchIdRef.current;
      if (!bid || !isPrintStationEnabled()) return;
      // Re-connect if socket dropped (forces new connection attempt).
      socketService.connect(bid);
      // Re-register in case the server restarted and cleared the registry.
      socketService.registerPrintStation(bid);
    }, REREGISTER_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []); // runs once on mount — reads latest branchId via ref

  return null;
};

export default RemotePrintSocketBridge;
